import Card from './Card';
import { Row, Col } from './Grid'
import CustomCtrl, { customFieldPersist } from './CustomCtrl';
import PropTypes from 'prop-types';
import { deepMerge, readField, writeField } from '../utils/objectUtilities';
import { safeEval } from "../utils/safeEval";
import { Modal } from 'react-bootstrap';
import { DefaultButton } from './DefaultButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GuideButton } from './GuideButton';
import ReactForm from './ReactForm';
import useCredentials from '../hooks/credentialsHook';
import Dropdown from './Dropdown';
import { useLazyMaterializeChronodata } from '../reducers/blobSlice';

export const makePersistValue = async ({ def, data }) => {
    const obj = {};
    for (const col of def.flatMap(section => section.rows.flat())) {
        const toSave = await customFieldPersist(col, data);
        if (toSave !== undefined) {
            deepMerge(obj, toSave);
        }
    }
    return obj;
}

/**
 * A dynamically generated form built using custom controls from a JSON form definition object.
 * @component
 */
const CustomForm = ({ def: defInput, data: dataInput, args: inputArgs, restricted = false, disabled = false, required = false, hideLabel = false, readonly = false, live = false, onChange, ...props }) => {
    const credentials = useCredentials();
    const def = useLazyMaterializeChronodata(defInput);
    const data = useLazyMaterializeChronodata(dataInput);
    const handleChange = useCallback((field, value) => {
        let update = {};
        if (field) {
            writeField(update, field, value);
        } else {
            update = { ...value }
        }
        onChange(update);
    }, [onChange]);

    const makeRow = (secIdx, rowIdx, children) => <Row className="my-2" key={secIdx + '_' + rowIdx}>{children}</Row>
    const makeCol = (secIdx, rowIdx, cellIdx, colspan, children) => <Col md={colspan} key={secIdx + '_' + rowIdx + '_' + cellIdx}>{children}</Col>
    const getAccess = useCallback(section => {
        const status = section?.statuses?.states.find(s => s.value == readField(data, section.statuses.field));
        return section?.access ?? status?.access;
    }, [data]);

    return def?.filter(section => {
        const access = getAccess(section);
        return !access || credentials.access.has(access);
    }).map((section, secIdx) => {
        if (section.cond && !(safeEval(`(section, data) => ${section.cond}`)(section, data))) {
            return null;
        }
        const access = getAccess(section);
        const enable = !access || (access.some(a => a.access) && credentials.access.hasFull(access.filter(a => a.access)));
        let form = section.rows.map((row, rowIdx) => {
            return makeRow(secIdx, rowIdx, row.filter(cell => {
                let args = { ...inputArgs };
                if (cell.args) {
                    Object.keys(cell.args).forEach(f => writeField(args, cell.field + '.' + f, readField(data, cell.args[f])));
                }
                if (cell.staticArgs) {
                    args = { ...args, [cell.field]: { ...args[cell.field], ...cell.staticArgs } };
                }
                const visible = cell.cond ? safeEval(`(def, data, args) => ${cell.cond}`)(cell, data, args) : true;
                return visible && cell.type !=='hidden'
            }).map((cell, cellIdx) => {
                let args = { ...inputArgs };
                if (cell.args) {
                    Object.keys(cell.args).forEach(f => writeField(args, cell.field + '.' + f, readField(data, cell.args[f])));
                }
                if (cell.staticArgs) {
                    args = { ...args, [cell.field]: { ...args[cell.field], ...cell.staticArgs } };
                }
                const argsToChain = cell.field ? args?.[cell.field] : args;
                return makeCol(secIdx, rowIdx, cellIdx, cell.colspan,
                    <CustomCtrl def={cell} live={live} disabled={!enable || disabled} readonly={readonly} restricted={restricted} required={required && cell.required} value={data} args={argsToChain} hideLabel={cell.hideLabel ?? hideLabel} onChange={(v, field) => handleChange(field ?? cell.field, v)}></CustomCtrl>
                );
            }));
        })

        const headerControls = [];
        if (def.guide) {
            headerControls.push(<GuideButton guide={def.guide}></GuideButton>)
        }

        if (section.statuses) {
            const value = readField(data, section.statuses.field);
            const state = section.statuses.states.find(s => s.value == value);
            const wildcard = !value && state?.name ? state.name : undefined;
            const options = section.statuses.states.filter(s => s.name !== wildcard && (!s.toSet || credentials.access.hasFull(s.toSet) || s.value === value));
            headerControls.push(<Dropdown hideLabel={true} wildcard={wildcard} options={options} labelField="name" valueField="value" value={readField(data, section.statuses.field)} onChange={value => handleChange(section.statuses.field, value)}></Dropdown>);
        }

        if (!section.borderless) {
            let title = section.title;
            let fixed = false;
            let headerStyle = section.style?.headerStyle;
            if (!section.title && headerControls.length) {
                title = " ";
                fixed = true;
                headerStyle = { backgroundColor: "transparent" };
            }
            form = <Card title={title} fixed={fixed} defaultCollapsed={section.collapse} headerControls={headerControls} headerStyle={headerStyle} bodyStyle={section.style?.bodyStyle} footerSyle={section.style?.footerStyle}>
                {form}
            </Card>
        } else if (headerControls.length) {
            form = <Card title=" " fixed={true} headerControls={headerControls} headerStyle={{ backgroundColor: "transparent" }}>{form}</Card>
        }
        
        return <div key={'sec_' + secIdx} {...props}>{form}</div>;
    })
}

CustomForm.propTypes = {
    /**
     * An array of form section objects.
     */
    def: PropTypes.arrayOf(PropTypes.object),

    /**
     * The object into which form field values are saved
     */
    data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),

    /**
     * An optional object of additional values which are passed to each control to facilitate customization
     */
    args: PropTypes.object,

    /**
     * Optional argument used to put the form into a semi-editable state used for two stage editablity
     */
    restricted: PropTypes.bool,

    /**
     * Force the control to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Collapses the floating label(s) used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Should text inputs update as they are edited
     */
    live: PropTypes.bool,

    /**
     * Function called whenever the control's value is updated with the new value
     */
    onChange: PropTypes.func
};

/**
 * Dialog to hold a custom form
 * @component
 */
export const FormDialog = ({ def, args, value, title, showModal, headerControls, footerControls, disabled = false, onClose, prohibitCancel = false, size = 'xlg' }) => {
    const [formData, setFormData] = useState(value);
    const [delta, setDelta] = useState({});
    const formRef = useRef();

    useEffect(() => {
        setFormData(value);
        setDelta({});
    }, [value]);

    const handleFormUpdate = update => {
        setFormData(formData => ({...formData, ...update}));
        setDelta(delta => ({...delta, ...update}));
    }

    const handleClose = () => {
        onClose(false)
    }

    const handleSave = () => {
        if (formRef.current?.checkValidity()) {
            onClose(Object.keys(delta)?.length > 0 ? delta : null);
        }
    }

    return <Modal show={showModal} onHide={() => handleClose(false)} size={size}>
        <Modal.Header closeButton={!prohibitCancel}>
            <Row className="w-100">
                <Col><Modal.Title>{title}</Modal.Title></Col>
                <Col xs="auto">{headerControls}</Col>
            </Row>
        </Modal.Header>
        <Modal.Body  style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
            <ReactForm ref={formRef}>
                <CustomForm def={def} data={formData} args={args} disabled={disabled} onChange={handleFormUpdate}></CustomForm>
            </ReactForm>
        </Modal.Body>
        <Modal.Footer>
            <Col xs>{footerControls}</Col>
            <Col xs="auto">
                { disabled ? null : <DefaultButton kind="save" onClick={() => handleSave(delta)}></DefaultButton> }
                { prohibitCancel && !disabled ? null : <DefaultButton kind="close" onClick={() => handleClose(false)}></DefaultButton> }
            </Col>
        </Modal.Footer>
    </Modal>
}

FormDialog.propTypes = {
    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed. Will pass the form data if save is pressed, null otherwise */
    onClose: PropTypes.func,

    /** Title for the dialog */
    title: PropTypes.node.isRequired,

    /**
     * A CustomForm definition that is used to capture data from which a value is computed for display.
     * The object has the following schema:
     * ```
     * {
     *  "label":            A label for the computed field in the UI
     *  "field":            a string naming the field where the form data object should be read and written
     *  "form":             a CustomFrom definition used to gather data
     *  "computation":      a function returning the contents for a div to display the result of processing the data gathered by
     *                      using the form (obj, field, args) => ...
     *  "styleComputation": a function returning an object used to style the div showing the result from the computation function
     *                      the signature is (obj, field, args) => ...
     * }
     * ```
     */
    def: PropTypes.array.isRequired,

    /**
     * The object holding the values to be displayed in the form.
     */
    value: PropTypes.object,

    /**
     * Value(s) to be passed to the embedded custom form args parameter
     */
    args: PropTypes.any,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Prevent the dialog from being closed without saving
     */
    prohibitCancel: PropTypes.bool
} 

export default CustomForm;